import React, { useEffect, useState, useMemo, useCallback } from "react";
import ProspectingCompaniesTable from "./ProspectingCompaniesTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../services/api/axios";
import BrandPagination from "../../../layouts/Pagination";
import SearchBar from "../../../shared/components/SearchBar";
import CompaniesModuleSwitcher from "../../../shared/components/CompaniesModuleSwitcher";
import {
  Box,
  Button,
  Card,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EntityProfile from "../../../shared/components/EntityProfile";
import DeleteConfirmDialog from "../../../shared/components/DeleteConfirmDialog";
import { prospectingCompanyProfileConfig } from "../../../shared/profile-configs/ProspectingCompanyProfileConfig";
import useProspectingCompaniesData from "./hooks/useProspectingCompaniesData";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import useRowSelection from "../../../hooks/useRowSelection";
import {
  downloadBlobFile,
  normalizePipelineStageFilter,
} from "./ProspectingCompaniesHelpers";
import ProspectingCompanyTags from "./ProspectingCompanyTags";
import {
  normalizeCity,
  normalizeCountry,
  normalizeState,
  normalizeString,
} from "../../../utils/filterNormalization";

import AddIcon from "@mui/icons-material/Add";
import {
  ACTION_MENU_ICON_SX,
  ACTION_MENU_ICON_TONES,
  ACTION_MENU_PAPER_SX,
  ACTIONS_HEADER_BTN_SX,
} from "../../../theme/buttonstyles";

const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: "1.5px solid #00336C",
  borderRadius: 2,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
  background: "#ffffff",
  overflow: "hidden",
};

const PIPELINE_STAGE_OPTIONS = [
  "New - Needs Outreach",
  "Outreach Sent",
  "In Conversation",
  "Qualified Opportunity",
  "Responded to RFI",
  "Closed Won",
  "Closed Lost",
  "Nurture / Not Now",
];

function ProspectingCompanies() {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const [filters, setFilters] = useState({
    sector: "",
    naics_name: "",
    city: "",
    state: "",
    country: "",
    tags: [],
    pipeline_stage: "",
  });

  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const [orderBy, setOrderBy] = useState("name");
  const [orderDirection, setOrderDirection] = useState("asc");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Selection
  const {
    selectedIds,
    selectedCount,
    isSelected,
    toggleOne,
    toggleMany,
    clearSelection,
  } = useRowSelection();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarSeverity(severity);
    setSnackbarMsg(message);
    setSnackbarOpen(true);
  }, []);

  const TAG_TOOLTIPS = useMemo(
    () => ({
      "High Intent":
        "Strong engagement signals such as repeat visits, longer time on site, or target industry alignment.",
      "Low Intent":
        "Some engagement, but not enough signals to indicate active project evaluation.",
      "Customer / Existing Employer":
        "Local company with repeat activity in your region.",
    }),
    [],
  );

  const { companies, totalCount, error, refetch, setCompanies } =
    useProspectingCompaniesData({
      page,
      pageSize,
      searchTerm: debouncedSearchTerm,
      filters,
      orderBy,
      orderDirection,
    });

  useEffect(() => {
    clearSelection();
  }, [companies, clearSelection]);

  const companyHasValidId = useCallback(
    (c) => c && c.id != null && c.id !== "",
    [],
  );

  useEffect(() => {
    const pipelineStage = normalizePipelineStageFilter(
      searchParams.get("pipeline_stage"),
    );
    const tags = searchParams.getAll("tags");
    const normalizedTags =
      tags.length > 0
        ? tags.map((tag) => tag.trim()).filter(Boolean)
        : searchParams
            .get("tags")
            ?.split(",")
            .map((tag) => tag.trim())
            .filter(Boolean) || [];

    setFilters((prev) =>
      prev.pipeline_stage === pipelineStage &&
      JSON.stringify(prev.tags || []) === JSON.stringify(normalizedTags)
        ? prev
        : {
            ...prev,
            pipeline_stage: pipelineStage,
            tags: normalizedTags,
          },
    );

    setPage(1);
  }, [searchParams]);

  const handleUpdateStage = useCallback(
    async (companyId, stage) => {
      try {
        if (stage === "Qualified Opportunity") {
          await api.post(`/prospects/${companyId}/mark-lead`);
        } else {
          await api.patch(
            `/prospecting-companies/${companyId}/pipeline-stage`,
            {
              pipeline_stage: stage,
            },
          );
        }

        await refetch();
      } catch (err) {
        showSnackbar("Failed to update prospect status.", "error");
      }
    },
    [refetch, showSnackbar],
  );

  const handleCompanyTagsUpdated = useCallback(
    (companyId, nextTags) => {
      setCompanies((prev) =>
        (prev || []).map((company) =>
          company?.id === companyId ? { ...company, tags: nextTags } : company,
        ),
      );

      setSelectedCompany((prev) =>
        prev?.id === companyId ? { ...prev, tags: nextTags } : prev,
      );
    },
    [setCompanies],
  );

  const companyProfileSections = useMemo(() => {
    if (!selectedCompany) return [];
    return prospectingCompanyProfileConfig(selectedCompany);
  }, [selectedCompany]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => {
      if (key === "country") {
        return {
          ...prev,
          country: value,
          state: "",
          tags: [],
        };
      }
      return { ...prev, [key]: value };
    });
    setPage(1);
  }, []);

  const handleLocationFilterChange = useCallback(({ city, state, country }) => {
    setFilters((prev) => ({
      ...prev,
      city: city || "",
      state: state || "",
      country: country || "",
    }));
    setPage(1);
  }, []);

  const handleExport = useCallback(() => {
    api
      .get("/prospecting-companies/export", { responseType: "blob" })
      .then((response) => {
        downloadBlobFile(response.data, "ProspectingCompanies.xlsx");
      })
      .catch(() => {
        showSnackbar("Export failed. Please try again.", "error");
      });
  }, [showSnackbar]);

  const handleOpenActionsMenu = useCallback((event) => {
    setActionsAnchorEl(event.currentTarget);
  }, []);

  const handleCloseActionsMenu = useCallback(() => {
    setActionsAnchorEl(null);
  }, []);

  const handleExportFromMenu = useCallback(() => {
    handleCloseActionsMenu();
    handleExport();
  }, [handleCloseActionsMenu, handleExport]);

  const uniqueValues = useCallback(
    (key) => {
      const normalizerByKey = {
        city: normalizeCity,
        state: normalizeState,
        country: normalizeCountry,
        sector: normalizeString,
        naics_name: normalizeString,
      };

      const normalizer = normalizerByKey[key] || ((value) => value);

      return [
        ...new Set(
          companies
            .map((c) => normalizer(c?.[key]))
            .filter((v) => v != null && v !== ""),
        ),
      ].sort((a, b) => String(a).localeCompare(String(b)));
    },
    [companies],
  );

  const uniqueTagValues = useMemo(() => {
    const set = new Set();
    companies.forEach((c) => {
      if (Array.isArray(c.tags)) {
        c.tags.forEach((t) => set.add(t));
      }
    });
    return Array.from(set).sort();
  }, [companies]);

  const handleSortRequest = useCallback(
    (key, explicitDirection = null) => {
      if (explicitDirection) {
        setOrderBy(key);
        setOrderDirection(explicitDirection);
      } else {
        const isAsc = orderBy === key && orderDirection === "asc";
        setOrderBy(key);
        setOrderDirection(isAsc ? "desc" : "asc");
      }
      setPage(1);
    },
    [orderBy, orderDirection],
  );

  const visibleCompanies = companies;
  const currentPageIds = useMemo(
    () => visibleCompanies.filter(companyHasValidId).map((c) => String(c.id)),
    [visibleCompanies, companyHasValidId],
  );
  const allCurrentSelected = useMemo(
    () =>
      currentPageIds.length > 0 && currentPageIds.every((id) => isSelected(id)),
    [currentPageIds, isSelected],
  );

  const someCurrentSelected = useMemo(
    () => currentPageIds.some((id) => isSelected(id)),
    [currentPageIds, isSelected],
  );

  const doDeleteCompanies = useCallback(
    async (ids) => {
      setDeleting(true);
      const results = await Promise.allSettled(
        ids.map((id) => api.delete(`/prospecting-companies/${id}`)),
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;

      if (succeeded > 0) {
        setSnackbarSeverity("success");
        setSnackbarMsg(
          `Deleted ${succeeded} compan${
            succeeded === 1 ? "y" : "ies"
          } successfully.`,
        );
      }
      if (failed > 0) {
        setSnackbarSeverity("warning");
        setSnackbarMsg(`Deleted ${succeeded}, but ${failed} failed.`);
      }
      setSnackbarOpen(true);
      setDeleting(false);
      setDeleteConfirmOpen(false);

      try {
        await refetch();
      } catch {
        showSnackbar(
          "Prospects were deleted, but the table failed to refresh.",
          "warning",
        );
      }
    },
    [refetch, showSnackbar],
  );

  const handleDeleteSelected = useCallback(() => {
    if (selectedIds.size === 0) {
      setSnackbarSeverity("info");
      setSnackbarMsg("No rows selected.");
      setSnackbarOpen(true);
      return;
    }
    setDeleteConfirmOpen(true);
  }, [selectedIds]);

  const confirmDeleteSelected = useCallback(async () => {
    const ids = Array.from(selectedIds);
    await doDeleteCompanies(ids);
    clearSelection();
  }, [clearSelection, doDeleteCompanies, selectedIds]);

  const handleClearSelectionFromMenu = useCallback(() => {
    handleCloseActionsMenu();
    clearSelection();
  }, [clearSelection, handleCloseActionsMenu]);

  const handleDeleteSelectedFromMenu = useCallback(async () => {
    handleCloseActionsMenu();
    await handleDeleteSelected();
  }, [handleCloseActionsMenu, handleDeleteSelected]);

  const columns = useMemo(
    () => [
      { id: "name", label: "Company", sortable: true },
      { id: "tags", label: "Tags", sortable: false },
      { id: "location", label: "Location", sortable: false },
      { id: "sector", label: "Sector", sortable: true },
      { id: "naics_name", label: "NAICS Text", sortable: true },
      { id: "explore", label: "Explore", sortable: false },
    ],
    [],
  );

  const renderCompanyTags = useCallback(
    (company) => (
      <ProspectingCompanyTags
        companyId={company.id}
        tags={company.tags || []}
        onTagsUpdated={handleCompanyTagsUpdated}
      />
    ),
    [handleCompanyTagsUpdated],
  );

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: PAGE_BG,
        pb: 4,
      }}
    >
      {/* ===== Sticky Header ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 1.75,
            flexWrap: "wrap",
          }}
        >
          {/* Left: icon + title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: BRAND_GREEN,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <BusinessIcon sx={{ fontSize: 19 }} />
            </Box>
            <CompaniesModuleSwitcher current="prospects" />
          </Box>

          {/* Center: search */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <SearchBar
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v);
                setPage(1);
              }}
              placeholder="Search Prospect Name, Sector, NAICS, or Location..."
              maxWidth={1700}
            />
          </Box>

          {/* Right: actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleOpenActionsMenu}
              sx={ACTIONS_HEADER_BTN_SX}
            >
              Actions
            </Button>

            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsMenuOpen}
              onClose={handleCloseActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
            >
              <MenuItem
                onClick={() => {
                  handleCloseActionsMenu();
                  navigate("/prospecting-companies/new");
                }}
              >
                <ListItemIcon
                  sx={{ ...ACTION_MENU_ICON_SX, ...ACTION_MENU_ICON_TONES.add }}
                >
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Prospect Company</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={handleExportFromMenu}
                disabled={companies.length === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.export,
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={handleClearSelectionFromMenu}
                disabled={selectedCount === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.clear,
                  }}
                >
                  <ClearAllIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Clear Selection</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={handleDeleteSelectedFromMenu}
                disabled={selectedCount === 0}
              >
                <ListItemIcon
                  sx={{
                    ...ACTION_MENU_ICON_SX,
                    ...ACTION_MENU_ICON_TONES.delete,
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{`Delete Selected (${selectedCount})`}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ===== Content Area ===== */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card sx={CARD_SX}>
          <ProspectingCompaniesTable
            companies={visibleCompanies}
            columns={columns}
            orderBy={orderBy}
            orderDirection={orderDirection}
            onSort={handleSortRequest}
            isSelected={isSelected}
            toggleOne={toggleOne}
            toggleMany={toggleMany}
            currentPageIds={currentPageIds}
            allCurrentSelected={allCurrentSelected}
            someCurrentSelected={someCurrentSelected}
            handleUpdateStage={handleUpdateStage}
            navigate={navigate}
            setSelectedCompany={setSelectedCompany}
            setProfileOpen={setProfileOpen}
            PIPELINE_STAGE_OPTIONS={PIPELINE_STAGE_OPTIONS}
            TAG_TOOLTIPS={TAG_TOOLTIPS}
            renderCompanyTags={renderCompanyTags}
            filters={filters}
            onFilterChange={handleFilterChange}
            onLocationFilterChange={handleLocationFilterChange}
            uniqueValues={uniqueValues}
            uniqueTagValues={uniqueTagValues}
          />
        </Card>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <BrandPagination
            page={page}
            count={Math.ceil(totalCount / pageSize) || 1}
            onChange={(_e, value) => setPage(value)}
          />
        </Box>
      </Box>

      <EntityProfile
        open={profileOpen}
        entity={selectedCompany}
        sections={companyProfileSections}
        dialogPaperSx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#00336C",
          border: "1px solid #00336C",
        }}
        onClose={() => {
          setProfileOpen(false);
          setSelectedCompany(null);
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteSelected}
        loading={deleting}
        title="Delete Companies"
        description={`Are you sure you want to delete ${selectedIds.size} prospect compan${selectedIds.size === 1 ? "y" : "ies"} from your CRM? This action can't be undone.`}
      />
    </Box>
  );
}

export default ProspectingCompanies;
